import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Plus, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { creditsCache } from "@/utils/creditsCache";
import { useAuth } from "@/hooks/useAuth";

interface CreditSummary {
  dailyBonusCredits: number;
  monthlyBonusCredits: number;
  rolloverCredits: number;
  storedCredits: number;
  totalBalance: number;
  lowCreditWarning: boolean;
}

// Match the existing creditsCache structure
interface CachedCreditSummary {
  dailyBonus: number;
  rollover: number;
  storedCredits: number;
  totalCredits: number;
  lowCreditWarning: boolean;
}

export const CreditBalanceWidget = () => {
  const [credits, setCredits] = useState<CreditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // EMERGENCY CIRCUIT BREAKER: Disable auto-loading credits
    if (user?.id) {
      fetchCredits();
    }
    setLoading(false);
  }, [user?.id]);

  const fetchCredits = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const creditData = await creditsCache.getCredits(user.id);
      
      // Transform the cached data to our component structure
      const transformedCredits: CreditSummary = {
        dailyBonusCredits: creditData.daily_bonus || 0,
        monthlyBonusCredits: creditData.monthly_base || 0,
        rolloverCredits: creditData.rollover || 0,
        storedCredits: creditData.stored || 0,
        totalBalance: creditData.total || 0,
        lowCreditWarning: creditData.low_credit_warning || false
      };
      
      setCredits(transformedCredits);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Credit Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded mb-2"></div>
            <div className="h-4 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!credits) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Credit Balance
        </CardTitle>
        <CardDescription>
          Used for voice calls and AI processing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Main Balance */}
        <div className="text-center">
          <div className="text-3xl font-bold text-brand-primary">
            {credits.totalBalance.toLocaleString()}
          </div>
          <div className="text-sm text-muted-foreground">Total Credits Available</div>
        </div>

        {/* Low Credit Warning */}
        {credits.lowCreditWarning && (
          <div className="flex items-center gap-2 p-3 bg-warning/20 text-warning-foreground rounded-lg">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Credit balance is running low</span>
          </div>
        )}

        {/* Simple Breakdown */}
        {(credits.dailyBonusCredits > 0 || credits.monthlyBonusCredits > 0 || credits.rolloverCredits > 0) && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Breakdown:</div>
            <div className="space-y-1 text-sm">
              {credits.dailyBonusCredits > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Daily Bonus</span>
                  <span>{credits.dailyBonusCredits.toLocaleString()}</span>
                </div>
              )}
              {credits.monthlyBonusCredits > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monthly Bonus</span>
                  <span>{credits.monthlyBonusCredits.toLocaleString()}</span>
                </div>
              )}
              {credits.rolloverCredits > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rollover</span>
                  <span>{credits.rolloverCredits.toLocaleString()}</span>
                </div>
              )}
              {credits.storedCredits > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Purchased</span>
                  <span>{credits.storedCredits.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button 
            className="w-full" 
            onClick={() => navigate("/credit-packages")}
            variant={credits.lowCreditWarning ? "default" : "outline"}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add More Credits
          </Button>
          <Button 
            variant="outline"
            size="sm"
            className="w-full" 
            onClick={() => navigate("/profile?tab=credits")}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Credit History
          </Button>
        </div>

      </CardContent>
    </Card>
  );
};